import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SubStoreContext = createContext();

export const useSubStores = () => {
  console.log('useSubStores: Hook called');
  const context = useContext(SubStoreContext);
  console.log('useSubStores: Context value:', context);
  if (!context) {
    console.error('useSubStores: Context is null/undefined');
    throw new Error('useSubStores must be used within a SubStoreProvider');
  }
  console.log('useSubStores: Returning context:', context);
  return context;
};

export const SubStoreProvider = ({ children }) => {
  console.log('SubStoreProvider: Initializing provider...');
  const [substores, setSubstores] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  // Load substores from localStorage or backend when authentication state changes
  useEffect(() => {
    async function initializeSubstores() {
      console.log('SubStoreProvider: Initializing...', { isAuthenticated, user });
      
      // Only proceed if user is authenticated
      if (!isAuthenticated || !user) {
        console.log('SubStoreProvider: User not authenticated, skipping initialization');
        setSubstores([]);
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      // Wait a bit to ensure token is available
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Clear any old/invalid substore data to start fresh
      const savedSubstores = localStorage.getItem('substores');
      console.log('SubStoreProvider: Saved substores:', savedSubstores);
      
      // If saved data exists, validate it first
      if (savedSubstores) {
        try {
          const parsed = JSON.parse(savedSubstores);
          // If it's not a valid array, clear it
          if (!Array.isArray(parsed) && !(parsed && Array.isArray(parsed.substores))) {
            console.log('SubStoreProvider: Clearing invalid substores data');
            localStorage.removeItem('substores');
          }
        } catch (e) {
          console.log('SubStoreProvider: Clearing corrupted substores data');
          localStorage.removeItem('substores');
        }
      }
      
      const token = localStorage.getItem('token');
      
      // Try to load from localStorage first for immediate display
      const currentSavedSubstores = localStorage.getItem('substores');
      if (currentSavedSubstores) {
        try {
          const parsed = JSON.parse(currentSavedSubstores);
          console.log('SubStoreProvider: Parsed substores:', parsed);
          
          // Handle both formats: direct array or API response format
          let substoresArray = [];
          if (Array.isArray(parsed)) {
            substoresArray = parsed;
          } else if (parsed && Array.isArray(parsed.substores)) {
            substoresArray = parsed.substores;
          }
          
          if (substoresArray.length > 0) {
            setSubstores(substoresArray);
            console.log('SubStoreProvider: Loaded from localStorage, fetching fresh data...');
          } else {
            console.log('SubStoreProvider: No valid substores in localStorage, will fetch from backend');
          }
        } catch (error) {
          console.error('SubStoreProvider: Error parsing saved substores:', error);
          localStorage.removeItem('substores');
        }
      }
      
      // Always fetch from backend when user is authenticated to ensure fresh data
      if (token) {
        try {
          console.log('SubStoreProvider: Fetching fresh data from backend...');
          const result = await import('../api/cacheAfterLogin').then((mod) => mod.cacheAfterLogin());
          console.log('SubStoreProvider: Cache result:', result);
          // The cacheAfterLogin returns { employees, substores } where substores is the array
          const substores = result.substores || [];
          console.log('SubStoreProvider: Substores loaded from backend:', substores);
          setSubstores(substores);
        } catch (err) {
          console.error('SubStoreProvider: Error loading substores from backend:', err);
          
          // Retry once after a short delay
          try {
            console.log('SubStoreProvider: Retrying after delay...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            const result = await import('../api/cacheAfterLogin').then((mod) => mod.cacheAfterLogin());
            const substores = result.substores || [];
            console.log('SubStoreProvider: Retry successful:', substores);
            setSubstores(substores);
          } catch (retryErr) {
            console.error('SubStoreProvider: Retry also failed:', retryErr);
            setSubstores([]);
          }
        }
      } else {
        console.warn('SubStoreProvider: No token found, cannot fetch from backend');
        setSubstores([]);
      }
      
      setLoading(false);
    }
    initializeSubstores();
  }, [isAuthenticated, user]);


  // Save substores to localStorage whenever they change
  useEffect(() => {
    if (substores.length > 0) {
      console.log('SubStoreProvider: Saving substores to localStorage:', substores);
      localStorage.setItem('substores', JSON.stringify(substores));
    }
  }, [substores]);

  const addSubStore = (newSubStore) => {
    setSubstores((prev) => [...prev, newSubStore]);
  };

  const removeSubStore = (index) => {
    setSubstores((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSubStore = (index, updatedSubStore) => {
    setSubstores((prev) => prev.map((store, i) => (i === index ? updatedSubStore : store)));
  };

  const refreshSubstores = async () => {
    setLoading(true);
    try {
      const result = await import('../api/cacheAfterLogin').then((mod) => mod.cacheAfterLogin());
      const substores = result.substores || [];
      setSubstores(substores);
    } catch (err) {
      console.error('SubStoreProvider: Error refreshing substores:', err);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    substores: substores || [],
    loading,
    addSubStore,
    removeSubStore,
    updateSubStore,
    refreshSubstores
  };

  console.log('SubStoreProvider: Current substores:', substores);
  console.log('SubStoreProvider: Rendering provider with children');

  return <SubStoreContext.Provider value={value}>{children}</SubStoreContext.Provider>;
};
